import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { _axios } from "../../../utils/axios";
import cx from 'classnames';
import { useEffect, useRef, useState } from "react";
import Dropdown from 'react-bootstrap/Dropdown';
import { useNavigate } from "react-router-dom";
import lineBetween from "../../../assets/images/btn/line.svg";
import lockIcon from "../../../assets/images/btn/lock.svg";
import logOutIcon from "../../../assets/images/btn/logout.svg";
import profileIcon from "../../../assets/images/btn/profile.svg";
import user from "../../../assets/images/user.svg";
import { BASE_URL } from "../../../constants/Constants";
import { useStuffToRender } from "../../../contexts/NavbarCommonSpace";
import { useSetUser, useUser } from "../../../contexts/UserContext";
import { useMediaQuery, useToggle } from "../../../hooks/common";
import { formatDate } from "../../../utils/date";
import { InfoBar } from "./InfoBar";
import style from "./navbar.module.css";
import { cookieUtils } from "../../../utils/cookieUtils";
import { resolveReviewerHome } from "./sidebar/visibleSidebarRoutes";


interface Notification {
  id: number;
  status: string;
  updated_at: string;
  application_id: string;
  message: string;
  updated_date: string;
}

const Navbar = ({ isSidebarOpen, toggleSidebar }: { isSidebarOpen: boolean, toggleSidebar: () => unknown }) => {
  const qc = useQueryClient();
  const userObj = useUser();
  const stuffToRender = useStuffToRender();
  const navigate = useNavigate();
  const setUser = useSetUser();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const anchor = useRef<HTMLButtonElement>(null);
  // const [isAdminMode, setIsAdminMode] = useState<boolean>(localStorage.getItem('isAdminMode') == 'true')
  const [isAdminMode, setIsAdminMode] = useState<boolean>(() => {
    const adminMode = cookieUtils.getSecureCookie('isAdminMode');
    return adminMode === "false" ? false : true;

  });
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [menu, toggleMenu] = useToggle();
  const [level1Notifications, setLevel1Notifications] = useState<Notification[]>([]);
  const [level2Notifications, setLevel2Notifications] = useState<Notification[]>([]);
  const [level3Notifications, setLevel3Notifications] = useState<Notification[]>([]);
  const [userNotifications, setUserNotifications] = useState<Notification[]>([]);
  const [userRegNotifications, setRegUserNotifications] = useState<Notification[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDropdownOpenWhite, setIsDropdownOpenWhite] = useState(false);
  const [isDropdownOpenLevel2, setIsDropdownOpenLevel2] = useState(false);
  const [isDropdownOpenLevel3, setIsDropdownOpenLevel3] = useState(false);
  const [isDropdownOpenUser, setIsDropdownOpenUser] = useState(false);
  const [isDropdownOpenUserReg, setIsDropdownOpenUserReg] = useState(false);
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [hasNewRegNotifications, setHasNewRegNotifications] = useState(false);
  const [isStuffDropdownOpen, setIsStuffDropdownOpen] = useState(false);
  const [existingLogo, setExistingLogo] = useState<string>('');
  const [logoUrl, setLogoUrl] = useState<string>('');

  useEffect(() => {
    const fetchImages = async () => {
      try {
        // Fetch logo
        const logoResponse = await _axios.get(`${BASE_URL}/api/logo`);
        if (logoResponse.data.success === 1) {
          setLogoUrl(logoResponse.data.url);
          if (logoResponse.data.filename) {
            setExistingLogo(logoResponse.data.filename);
          }
        }
      } catch (error) {
        console.error('Error fetching images:', error);
      }
    };

    fetchImages();
  }, []);

  const handleOutsideClick = (event: { target: Node | null; }) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsDropdownOpen(false);
    }
  };

  const isArchitectOrUser = ["user", "architect"].includes(
    userObj?.role?.toLowerCase() || ""
  );

  const isAdmin = userObj?.role?.toLowerCase()?.includes("admin");

  // const onAdminMode = () => {
  //   const isAdminMode = localStorage.getItem('isAdminMode')    
  //   if (isAdminMode === 'true') {
  //     navigate('/level-three-dashboard')
  //     localStorage.setItem('isAdminMode', 'false')
  //     setIsAdminMode(false)
  //   }
  //   else {
  //     navigate('/dashboard')
  //     localStorage.setItem('isAdminMode', 'true')
  //     setIsAdminMode(true)
  //   }
  // }

  /**
   * Leaving admin mode shows the Level 3 sidebar, so the Admin lands on its
   * first entry that is still shown: the Dashboard, or User Approvals when
   * the Dashboard is switched off for Level 3 in Sidebar Menu Config.
   *
   * Before: leaving admin mode always landed on the Level 3 Dashboard.
   *
   * const onAdminMode = () => {
   *   const newAdminMode = !isAdminMode;
   *   cookieUtils.setSecureCookie('isAdminMode', newAdminMode.toString());
   *   setIsAdminMode(newAdminMode);
   *   if (newAdminMode) {
   *     navigate('/dashboard');
   *   } else {
   *     navigate('/level-three-dashboard');
   *   }
   * };
   */
  const onAdminMode = async () => {
    const newAdminMode = !isAdminMode;
    cookieUtils.setSecureCookie('isAdminMode', newAdminMode.toString());
    setIsAdminMode(newAdminMode);
    if (newAdminMode) {
      navigate('/dashboard');
    } else {
      navigate(await resolveReviewerHome('Level 3'));
    }
  };

  useEffect(() => {
    const fetchLevel1Notifications = async () => {
      try {
        const response = await _axios.post(`${BASE_URL}/api/bellMessage/application`);
        const sortedNotifications = response.data.data.sort(
          (a: Notification, b: Notification) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        );
        setLevel1Notifications(sortedNotifications);
        if (sortedNotifications.length > 0) {
          setHasNewNotifications(true);
        }
      } catch (error) {
        console.error('Error fetching level 1 notifications', error);
      }

    };

    fetchLevel1Notifications();
  }, []);

  useEffect(() => {
    const fetchLevel2Notifications = async () => {
      if (userObj?.role?.toLowerCase()?.includes("level 2")) {
        try {
          const response = await _axios.post(`${BASE_URL}/api/bellMessage/application/level2`);
          const sortedNotifications = response.data.data.sort(
            (a: Notification, b: Notification) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
          );
          setLevel2Notifications(sortedNotifications);
          if (sortedNotifications.length > 0) {
            setHasNewNotifications(true);
          }
        } catch (error) {
          console.error('Error fetching level 2 notifications', error);
        }
      }
    };

    fetchLevel2Notifications();
  }, [userObj]);

  useEffect(() => {
    const fetchLevel3Notifications = async () => {
      if (userObj?.role?.toLowerCase()?.includes("level 3") || userObj?.role?.toLowerCase()?.includes("system admin")) {
        try {
          const response = await _axios.post(`${BASE_URL}/api/bellMessage/application/level3`);
          const sortedNotifications = response.data.data.sort(
            (a: Notification, b: Notification) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
          );
          setLevel3Notifications(sortedNotifications);
          if (sortedNotifications.length > 0) {
            setHasNewNotifications(true);
          }
        } catch (error) {
          console.error('Error fetching level 3 notifications', error);
        }
      }
    };

    fetchLevel3Notifications();
  }, [userObj]);

  useEffect(() => {
    const fetchUserNotifications = async () => {
      if (userObj?.role?.toLowerCase()?.includes("user")) {
        try {
          const response = await _axios.post(`${BASE_URL}/api/bellMessage/application/user`, { user_id: userObj.user_id });
          const sortedNotifications = response.data.data.sort(
            (a: Notification, b: Notification) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
          );
          setUserNotifications(sortedNotifications);
          if (sortedNotifications.length > 0) {
            setHasNewNotifications(true);
          }
        } catch (error) {
          console.error('Error fetching user notifications', error);
        }
      }
    };

    fetchUserNotifications();
  }, [userObj]);

  useEffect(() => {
    const getRegistrationDetailsForUser = async () => {
      if (userObj?.role?.toLowerCase()?.includes("user")) {
        try {
          const response = await _axios.post(`${BASE_URL}/api/bellMessage/registration/User`, { user_id: userObj.user_id });
          const sortedNotifications = response.data.data.sort(
            (a: Notification, b: Notification) => new Date(b.updated_date).getTime() - new Date(a.updated_date).getTime()
          );
          setRegUserNotifications(sortedNotifications);
          if (sortedNotifications.length > 0) {
            setHasNewRegNotifications(true); // Set the flag to true if there are new registration notifications
          } else {
            setHasNewRegNotifications(false); // Set the flag to false if there are no new registration notifications
          }
        } catch (error) {
          console.error('Error fetching user registration notifications', error);
        }
      }
    };

    getRegistrationDetailsForUser();
  }, [userObj]);

  const updateNotificationStatus = async (application_id: any, notificationType: any) => {
    try {
      const response = await _axios.post(`${BASE_URL}/api/bellMessage/application/update-notification`, {
        application_id,
        notificationType
      });
      console.log("Notification status updated:", response.data);
    } catch (error) {
      console.error("Error updating notification status:", error);
    }
  };

  const formatDateToMySQL = (isoDate: string | number | Date) => {
    return new Date(isoDate).toISOString().slice(0, 19).replace('T', ' ');
  };


  const updateUserRegNotificationStatus = async (id: string) => {
    try {


      const response = await _axios.post(`${BASE_URL}/api/bellMessage/user-verification-comments/update-notification`, {
        id: id
      });
      console.log("User registration notification status updated:", response.data);
    } catch (error) {
      console.error("Error updating user registration notification status:", error);
    }
  };

  const toggleStuffDropdown = () => {
    setIsStuffDropdownOpen(!isStuffDropdownOpen);
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setIsDropdownOpenLevel2(false);
        setIsDropdownOpenLevel3(false);
        setIsDropdownOpenUser(false);
        setIsDropdownOpenUserReg(false);
        setIsDropdownOpenWhite(false);

      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const toggleDropdownWhite = () => {
    setIsDropdownOpenWhite(!isDropdownOpenWhite);

  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
    setHasNewNotifications(false);
  };

  const toggleDropdownLevel2 = () => {
    setIsDropdownOpenLevel2(!isDropdownOpenLevel2);
    setHasNewNotifications(false);
  };

  const toggleDropdownLevel3 = () => {
    setIsDropdownOpenLevel3(!isDropdownOpenLevel3);
    setHasNewNotifications(false);
  };

  const toggleDropdownUser = () => {
    setIsDropdownOpenUser(!isDropdownOpenUser);
    setIsDropdownOpenUserReg(!isDropdownOpenUserReg);
    setHasNewNotifications(false);
  };


  const removeNotification = (
    index: number,
    isLevel2 = false,
    isLevel3 = false,
    isUser = false,
    isUserReg = false,
    notificationId?: string
  ) => {
    let notificationToRemove;
    let notificationType;

    if (isLevel2) {
      notificationToRemove = level2Notifications[index];
      setLevel2Notifications(level2Notifications.filter((_, i) => i !== index));
      notificationType = 'leveltwo_notification';
    } else if (isLevel3) {
      notificationToRemove = level3Notifications[index];
      setLevel3Notifications(level3Notifications.filter((_, i) => i !== index));
      notificationType = 'levelthree_notification';
    } else if (isUser) {
      notificationToRemove = userNotifications[index];
      setUserNotifications(userNotifications.filter((_, i) => i !== index));
      notificationType = 'user_notification';
    } else if (isUserReg) {
      notificationToRemove = userRegNotifications[index];
      setRegUserNotifications(userRegNotifications.filter((_, i) => i !== index));
      notificationType = 'user_reg_notification';
      updateUserRegNotificationStatus(notificationId as string);
      return;
    } else {
      notificationToRemove = level1Notifications[index];
      setLevel1Notifications(level1Notifications.filter((_, i) => i !== index));
      notificationType = 'levelone_notification';
    }

    updateNotificationStatus(notificationToRemove.application_id, notificationType);
  };


  const dropdownContent = (
    <div className={style.dropdownBox} ref={dropdownRef}>
      <ul>
        <Dropdown.Menu show className={`align-left ${style.DropMenuWidth}`} style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {level1Notifications.length > 0 ? (
            level1Notifications.slice(0, 7).map((notification, index) => (
              <div className={style.dropContent} key={index}>
                <div className={style.cardContent}>
                  <div className={style.ImgLeftBox}>
                    <img
                      src={user}
                      alt="profile-pic"
                      className="h-[40px] w-[40px] rounded-full "
                    />
                  </div>
                  <div className={style.ContextText}>
                    <div className={style.TopDetails}>
                      {notification.message}
                    </div>
                    <div className={style.BottomDate}>
                      {formatDate(new Date(notification.updated_at))}
                    </div>
                  </div>
                  <i className={`${style.closeButton} fa fa-close`} onClick={() => removeNotification(index)}></i>
                </div>
              </div>
            ))
          ) : (
            <center style={{ marginTop: '5px' }}>
              <div className={style.dropContent}>No notifications available</div>
            </center>
          )}
        </Dropdown.Menu>
      </ul>
    </div>
  );


  const dropdownContentLevel2 = (
    <div className={style.dropdownBox} ref={dropdownRef}>
      <ul>
        <Dropdown.Menu show className={`align-left ${style.DropMenuWidth}`} style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {level2Notifications.length > 0 ? (
            level2Notifications.slice(0, 7).map((notification, index) => (
              <div className={style.dropContent} key={index}>
                <div className={style.cardContent}>
                  <div className={style.ImgLeftBox}>
                    <img
                      src={user}
                      alt="profile-pic"
                      className="h-[40px] w-[40px] rounded-full "
                    />
                  </div>
                  <div className={style.ContextText}>
                    <div className={style.TopDetails}>
                      {notification.message}
                    </div>
                    <div className={style.BottomDate}>
                      {formatDate(new Date(notification.updated_at))}
                    </div>
                  </div>
                  <i className={`${style.closeButton} fa fa-close`} onClick={() => removeNotification(index, true)}></i>
                </div>
              </div>
            ))
          ) : (
            <center style={{ marginTop: '5px' }}>
              <div className={style.dropContent}>No notifications available</div>
            </center>

          )}
        </Dropdown.Menu>
      </ul>
    </div>
  );

  const dropdownContentLevel3 = (
    <div className={style.dropdownBox} ref={dropdownRef}>
      <ul>
        <Dropdown.Menu show className={`align-left ${style.DropMenuWidth}`} style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {level3Notifications.length > 0 ? (
            level3Notifications.slice(0, 7).map((notification, index) => (
              <div className={style.dropContent} key={index}>
                <div className={style.cardContent}>
                  <div className={style.ImgLeftBox}>
                    <img
                      src={user}
                      alt="profile-pic"
                      className="h-[40px] w-[40px] rounded-full "
                    />
                  </div>
                  <div className={style.ContextText}>
                    <div className={style.TopDetails}>
                      {notification.message}
                    </div>
                    <div className={style.BottomDate}>
                      {formatDate(new Date(notification.updated_at))}
                    </div>
                  </div>
                  <i className={`${style.closeButton} fa fa-close`} onClick={() => removeNotification(index, false, true)}></i>
                </div>
              </div>
            ))
          ) : (
            <center style={{ marginTop: '5px' }}>
              <div className={style.dropContent}>No notifications available</div>
            </center>
          )}
        </Dropdown.Menu>
      </ul>
    </div>
  );

  const dropdownContentUser = (
    <div className={style.dropdownBox} ref={dropdownRef}>
      <ul>
        <Dropdown.Menu show className={`align-left ${style.DropMenuWidth}`} style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {userNotifications.length > 0 ? (
            userNotifications.slice(0, 7).map((notification, index) => (
              <div className={style.dropContent} key={index}>
                <div className={style.cardContent}>
                  <div className={style.ImgLeftBox}>
                    <img
                      src={user}
                      alt="profile-pic"
                      className="h-[40px] w-[40px] rounded-full "
                    />
                  </div>
                  <div className={style.ContextText}>
                    <div className={style.TopDetails}>
                      {notification.message}
                    </div>
                    <div className={style.BottomDate}>
                      {formatDate(new Date(notification.updated_at))}
                    </div>
                  </div>
                  <i className={`${style.closeButton} fa fa-close`} onClick={() => removeNotification(index, false, false, true)}></i>
                </div>
              </div>
            ))
          ) : (
            <center style={{ marginTop: '5px' }}>
              <div className={style.dropContent}>No notifications available</div>
            </center>

          )}
        </Dropdown.Menu>
      </ul>
    </div>
  );

  const dropdownContentUserReg = (
    <div className={style.dropdownBox} ref={dropdownRef}>
      <ul>
        <Dropdown.Menu show className={`align-left ${style.DropMenuWidth}`} style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {userRegNotifications.length > 0 ? (
            userRegNotifications.slice(0, 7).map((notification, index) => (
              <div className={style.dropContent} key={index}>
                <div className={style.cardContent}>
                  <div className={style.ImgLeftBox}>
                    <img
                      src={user}
                      alt="profile-pic"
                      className="h-[40px] w-[40px] rounded-full "
                    />
                  </div>
                  <div className={style.ContextText}>
                    <div className={style.TopDetails}>
                      {notification.message}
                    </div>
                    <div className={style.BottomDate}>
                      {formatDate(new Date(notification.updated_date))}
                    </div>
                  </div>
                  <i className={`${style.closeButton} fa fa-close`} onClick={() => removeNotification(index, false, false, false, true, notification.id.toString())}></i>

                </div>
              </div>
            ))
          ) : (
            <center style={{ marginTop: '5px' }}>
              <div className={style.dropContent}>No notifications available</div>
            </center>
          )}
        </Dropdown.Menu>
      </ul>
    </div>
  );

  const clearAllCookies = () => {
    // Clear all known cookies
    
    cookieUtils.removeSecureCookie('userRegistrationDetails');
    cookieUtils.removeSecureCookie('userDetails');
    cookieUtils.removeSecureCookie('token');
    cookieUtils.removeSecureCookie('subRole');
    // Add any other cookies that need to be cleared
  };
  
  const handleLogoClick = () => {
    navigate('/launch-page');
    // clearAllCookies();
  };

  

  return (
    <>
      {isArchitectOrUser && <InfoBar />}
      <div className={`${style.navbar} border-b-2 box-border border-gray-200 pt-3 pb-3 px-3 md:pr-4 relative`}>

        <div className="flex items-center justify-between w-full">
          <div className="flex items-center ">
            {isMobile && (
              <button onClick={toggleSidebar} className="mr-2">
                <i className={cx("!m-0 fa w-5 text-xl", {
                  ['fa-align-left']: !isSidebarOpen,
                  ['fa-close']: isSidebarOpen
                })}></i>
              </button>
            )}
            {logoUrl && (
              <img
                src={logoUrl}
                alt="Logo"
                className={`${style.companyIcon}`}
                onClick={handleLogoClick}
                style={{ cursor: 'pointer' }}
              />
            )}
          </div>
          {/* <div className="grow md:hidden"></div> */}

          {/* <div className="hidden grow md:flex">{stuffToRender}</div> */}




          <div className="flex items-center">
            {isAdmin && (
              <div className={style.adminModeToggle}>
                Admin Mode
                <br />

                <div className={style.footerActive}>
                  <label className={style.footerActive}></label>
                  <label className={style.toggleBtn}>
                    <input type="checkbox" checked={isAdminMode} onChange={onAdminMode} className={style.inputCheckbox} />
                    <span className={style.slider}></span>

                  </label>
                </div>
              </div>
            )}

            {/* {stuffToRender &&
              <div className="grow md:hidden">
                <button onClick={toggleStuffDropdown} className="ml-auto mt-auto right-0">
                  <i className="fa fa-ellipsis-v"></i>
                </button>
              </div>
            } */}
            <div className="hidden grow md:flex">{stuffToRender}</div>


            {isMobile && isStuffDropdownOpen && (
              <div className="absolute right-1 top-full mt-1 p-2 bg-white shadow-md z-10 w-[98%]">
                {stuffToRender}
              </div>
            )}

            {userObj?.role?.toLowerCase()?.includes("level 1") && (
              <>
                <div className="mx-2 md:mx-4">
                  <div className={`relative ${style.bellBox}`}>
                    <div
                      className={`${style.navIconDiv} ${hasNewNotifications ? style.newNotification : ""}`}
                      onClick={toggleDropdown}
                      style={{ position: 'relative', zIndex: 2 }}
                    >
                      {level1Notifications.length > 0 ? (
                        <>
                          <i className={`fa fa-bell-o text-primary ${isMobile ? 'text-lg' : 'text-xl'}`}></i>
                          {hasNewNotifications && <div className={style.notificationDot}></div>}
                        </>
                      ) : (
                        <i className={`fa fa-bell-o text-primary ${isMobile ? 'text-lg' : 'text-xl'}`}></i>
                      )}
                    </div>
                    {isDropdownOpen && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1 }}>
                        {dropdownContent}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {userObj?.role?.toLowerCase()?.includes("level 2") && (
              <>
                <div className="mx-2 md:mx-4">
                  <div className={`relative ${style.bellBox}`}>
                    <div
                      className={`${style.navIconDiv} ${hasNewNotifications ? style.newNotification : ""}`}
                      onClick={toggleDropdownLevel2}
                      style={{ position: 'relative', zIndex: 2 }}
                    >
                      {level2Notifications.length > 0 ? (
                        <>
                          <i className={`fa fa-bell-o text-primary ${isMobile ? 'text-lg' : 'text-xl'}`}></i>
                          {hasNewNotifications && <div className={style.notificationDot}></div>}
                        </>
                      ) : (
                        <i className={`fa fa-bell-o text-primary ${isMobile ? 'text-lg' : 'text-xl'}`}></i>
                      )}
                    </div>
                    {isDropdownOpenLevel2 && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1 }}>
                        {dropdownContentLevel2}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {userObj?.role?.toLowerCase()?.includes("level 3") && (
              <>
                <div className="mx-2 md:mx-4">
                  <div className={`relative ${style.bellBox}`}>
                    <div
                      className={`${style.navIconDiv} ${hasNewNotifications ? style.newNotification : ""}`}
                      onClick={toggleDropdownLevel3}
                      style={{ position: 'relative', zIndex: 2 }}
                    >
                      {level3Notifications.length > 0 ? (
                        <>
                          <i className={`fa fa-bell-o text-primary ${isMobile ? 'text-lg' : 'text-xl'}`}></i>
                          {hasNewNotifications && <div className={style.notificationDot}></div>}
                        </>
                      ) : (
                        <i className={`fa fa-bell-o text-primary ${isMobile ? 'text-lg' : 'text-xl'}`}></i>
                      )}
                    </div>
                    {isDropdownOpenLevel3 && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1 }}>
                        {dropdownContentLevel3}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {(userObj?.role?.toLowerCase()?.includes("admin")) && !isAdminMode && (
              <>
                <div className="mx-2 md:mx-4">
                  <div className="relative">
                    <div
                      className={`${style.navIconDiv} ${hasNewNotifications ? style.newNotification : ""}`}
                      onClick={toggleDropdownLevel3}
                      style={{ position: 'relative', zIndex: 2 }}
                    >
                      {level3Notifications.length > 0 ? (
                        <>
                          <i className={`fa fa-bell-o text-primary ${isMobile ? 'text-lg' : 'text-xl'}`}></i>
                          {hasNewNotifications && <div className={style.notificationDot}></div>}
                        </>
                      ) : (
                        <i className={`fa fa-bell-o text-primary ${isMobile ? 'text-lg' : 'text-xl'}`}></i>
                      )}
                    </div>
                    {isDropdownOpenLevel3 && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1 }}>
                        {dropdownContentLevel3}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {userObj?.role?.toLowerCase()?.includes('user') && (
              <>
                <div className="mx-2 md:mx-4">
                  <div className={`relative ${style.bellBox}`}>
                    <div
                      className={`${style.navIconDiv} ${(hasNewNotifications || hasNewRegNotifications) ? style.newNotification : ''}`}
                      onClick={toggleDropdownUser}
                      style={{ position: 'relative', zIndex: 2 }}
                    >
                      {(userNotifications.length > 0 || userRegNotifications.length > 0) ? (
                        <>
                          <i className={`fa fa-bell-o text-primary ${isMobile ? 'text-lg' : 'text-xl'}`}></i>
                          {(hasNewNotifications || hasNewRegNotifications) && <div className={style.notificationDot}></div>}
                        </>
                      ) : (
                        <i className={`fa fa-bell-o text-primary ${isMobile ? 'text-lg' : 'text-xl'}`}></i>
                      )}
                    </div>
                    {userNotifications.length > 0 ? (
                      isDropdownOpenUser && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1 }}>
                          {dropdownContentUser}
                        </div>
                      )
                    ) : (
                      isDropdownOpenUserReg && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1 }}>
                          {dropdownContentUserReg}
                        </div>
                      )
                    )}
                  </div>
                </div>
              </>
            )}

            <div className={`flex items-center ${isMobile ? 'space-x-3' : 'space-x-4'} ${style.UserProfileDropdown}`}>

              <img
                src={user}
                alt="profile-pic"
                className={`rounded-full ${isMobile ? 'h-[28px] w-[28px]' : 'h-[40px] w-[40px]'}`}
              />
              {!isMobile && (
                <div className={`${style.adminName}`}>
                  <p className="text-grey-600 text-base capitalize">
                    {userObj?.name || "Unknown User"}
                  </p>
                  {!!userObj?.role && (
                    <p className="text-gray-400 text-sm capitalize">
                      {userObj?.role}
                    </p>
                  )}
                </div>
              )}
              <>
                <div className="relative inline-block text-left">
                  <button
                    ref={anchor}
                    className={`bg-gray-200 rounded ${isMobile ? 'px-2 py-0.75' : 'px-2 '}`}
                    onClick={(e) => setIsDropdownOpenWhite(!isDropdownOpenWhite)}
                  >
                    <i className={`fa fa-caret-down m-0 ${isMobile ? 'text-base' : ''}`}></i>
                  </button>

                  {isDropdownOpenWhite && (
                    <div
                      className={`${style.dropdownBox} p-1 origin-top-right absolute right-0 mt-2 w-48 rounded-md bg-white`}
                      style={{ boxShadow: '0px 1px 6px 0px #00000040' }}
                      ref={dropdownRef}
                    >
                      <div className="py-0" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                        <button
                          onClick={() => {
                            navigate("/user-profile");
                            toggleMenu();
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 hover:text-gray-900 flex items-center rounded-t-md"
                          role="menuitem"
                        >
                          <img src={profileIcon} alt="Profile" className="mr-2" />
                          Profile
                        </button>
                        <img src={lineBetween} alt="firstLine" className="ml-1" />

                        <div className={style.greyLine}></div>
                        <button
                          onClick={() => {
                            navigate("/onboarding/change-password");
                            toggleMenu();
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 hover:text-gray-900 flex items-center"
                          role="menuitem"
                        >
                          <img src={lockIcon} alt="Change Password" className="mr-2" />
                          Change Password
                        </button>
                        <img src={lineBetween} alt="firstLine" className="ml-1" />

                        <button
                          className="block w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 hover:text-gray-900 flex items-center rounded-b-md"
                          role="menuitem"
                          onClick={() => {
                            qc.clear();
                            setUser(null);
                            localStorage.clear();
                            handleLogoClick();
                            navigate("/onboarding/login");
                          }}
                        >
                          <img src={logOutIcon} alt="Logout" className="mr-2" />
                          Logout
                        </button>
                      </div>
                    </div>
                  )}

                </div>

              </>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
